import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAccess } from "@/lib/access";
import VendorFormDialog from "@/components/VendorFormDialog";
import VendorPocDialog from "@/components/VendorPocDialog";
import VendorSidePanel from "@/components/vendor/VendorSidePanel";
import VendorAttachmentsTabs from "@/components/vendor/VendorAttachmentsTabs";
import VendorCompliancePanel from "@/components/vendor/VendorCompliancePanel";
import VendorFinancialSummary from "@/components/vendor/VendorFinancialSummary";
import VendorAuditHistory from "@/components/vendor/VendorAuditHistory";
import { getGradeStyle } from "@/components/vendor/gradeStyles";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ArrowLeft,
  PencilSimple,
  Trash,
  Storefront,
  ShieldCheck,
  Paperclip,
  ClockCounterClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUSES = ["Started", "In-Progress", "Partial", "Completed", "AMC"];

function initials(name) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean).slice(0, 2);
  const chars = parts.map((w) => w[0]?.toUpperCase()).join("");
  return chars || "?";
}

export default function VendorDetail() {
  const { can } = useAccess();
  const canEdit = can("vendor.edit");
  const { id } = useParams();
  const navigate = useNavigate();
  const [vendor, setVendor] = useState(null);
  const [generalFiles, setGeneralFiles] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [pocOpen, setPocOpen] = useState(false);
  const [editingPoc, setEditingPoc] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [v, g, inv] = await Promise.all([
        api.get(`/vendors/${id}`),
        api.get(`/vendors/${id}/attachments`, { params: { kind: "general" } }),
        api.get(`/vendors/${id}/attachments`, { params: { kind: "invoice" } }),
      ]);
      setVendor(v.data);
      setGeneralFiles(g.data);
      setInvoices(inv.data);
    } catch {
      toast.error("Failed to load vendor");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const changeStatus = async (s) => {
    try {
      const { data } = await api.put(`/vendors/${id}`, { status: s });
      setVendor(data);
      toast.success(`Status set to ${s}`);
    } catch {
      toast.error("Failed");
    }
  };

  const onEditSubmit = async (payload) => {
    try {
      const { data } = await api.put(`/vendors/${id}`, payload);
      setVendor(data);
      setEditOpen(false);
      toast.success("Vendor updated");
    } catch {
      toast.error("Update failed");
    }
  };

  const removeVendor = async () => {
    if (!window.confirm("Delete this vendor and all attachments?")) return;
    try {
      await api.delete(`/vendors/${id}`);
      toast.success("Vendor deleted");
      navigate("/inventory/vendors");
    } catch {
      toast.error("Delete failed");
    }
  };

  // PoCs
  const submitPoc = async (form) => {
    try {
      if (editingPoc) {
        await api.put(`/vendors/${id}/pocs/${editingPoc.id}`, form);
        toast.success("PoC updated");
      } else {
        await api.post(`/vendors/${id}/pocs`, form);
        toast.success("PoC added");
      }
      setPocOpen(false);
      setEditingPoc(null);
      load();
    } catch {
      toast.error("Failed to save PoC");
    }
  };
  const deletePoc = async (pocId) => {
    if (!window.confirm("Remove this PoC?")) return;
    try {
      await api.delete(`/vendors/${id}/pocs/${pocId}`);
      toast.success("PoC removed");
      load();
    } catch {
      toast.error("Failed");
    }
  };

  // Attachments
  const uploadAttachment = async (file, kind) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/vendors/${id}/attachments`, fd, {
        params: { kind },
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Uploaded");
      load();
    } catch {
      toast.error("Upload failed");
    }
  };
  const downloadAttachment = async (att) => {
    try {
      const res = await api.get(`/vendor-attachments/${att.id}/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = att.file_name;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };
  const deleteAttachment = async (aid) => {
    try {
      await api.delete(`/vendor-attachments/${aid}`);
      toast.success("Removed");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  if (loading || !vendor) {
    return (
      <div className="p-8 text-slate-500" data-testid="vendor-detail-loading">
        Loading…
      </div>
    );
  }

  const auto = vendor.auto_score || {};
  const scoreVal = auto.value ?? null;
  const grade = auto.grade;
  const gradeStyle = getGradeStyle(grade);

  return (
    <div data-testid="vendor-detail-page">
      <div className="glass-header sticky top-0 z-10 px-8 py-5">
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => navigate("/inventory/vendors")}
            className="p-2 rounded-md hover:bg-slate-100 text-slate-600"
            data-testid="back-to-vendors"
          >
            <ArrowLeft size={18} />
          </button>
          <div
            className="w-11 h-11 rounded-full bg-blue-600 text-white flex items-center justify-center font-heading font-bold shrink-0"
            aria-hidden="true"
          >
            {initials(vendor.name)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
              Vendor
            </div>
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1
                className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900"
                data-testid="vendor-detail-name"
              >
                {vendor.name}
              </h1>
              {scoreVal !== null && (
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${gradeStyle.chip}`}
                  data-testid="vendor-score-chip"
                  title="Auto score, from the Compliance & Score tab"
                >
                  {scoreVal}/100 · {grade}
                </span>
              )}
            </div>
          </div>
          <Select value={vendor.status} onValueChange={changeStatus} disabled={!canEdit}>
            <SelectTrigger
              className="w-44 bg-white border-slate-200 h-10"
              data-testid="vendor-status-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {canEdit && (<>
          <button
            onClick={() => setEditOpen(true)}
            className="inline-flex items-center gap-2 px-3 h-10 rounded-md border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50"
            data-testid="edit-vendor-button"
          >
            <PencilSimple size={16} /> Edit
          </button>
          <button
            onClick={removeVendor}
            className="inline-flex items-center gap-2 px-3 h-10 rounded-md border border-red-100 bg-white text-red-600 text-sm font-medium hover:bg-red-50"
            data-testid="delete-vendor-button"
          >
            <Trash size={16} />
          </button>
          </>)}
        </div>
      </div>

      <div className="p-8">
        <Tabs defaultValue="overview" className="w-full">
          <TabsList
            className="h-auto bg-white border border-slate-200 rounded-md p-1 mb-6"
            data-testid="vendor-detail-tabs"
          >
            <TabsTrigger value="overview" data-testid="tab-overview" className="px-4 py-2">
              <Storefront size={14} className="mr-2" /> Overview
            </TabsTrigger>
            <TabsTrigger value="compliance" data-testid="tab-compliance" className="px-4 py-2">
              <ShieldCheck size={14} className="mr-2" /> Compliance & Score
            </TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents" className="px-4 py-2">
              <Paperclip size={14} className="mr-2" />
              Documents ({generalFiles.length + invoices.length})
            </TabsTrigger>
            <TabsTrigger value="activity" data-testid="tab-activity" className="px-4 py-2">
              <ClockCounterClockwise size={14} className="mr-2" /> Activity
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-0">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <VendorFinancialSummary vendor={vendor} />
                <div className="bg-white border border-slate-200 rounded-lg p-6">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                    Purpose / Scope
                  </div>
                  <p className="mt-2 text-sm text-slate-700 whitespace-pre-wrap">
                    {vendor.purpose || (
                      <span className="text-slate-400 italic">No description</span>
                    )}
                  </p>
                </div>
              </div>

              <VendorSidePanel
                vendor={vendor}
                onAddPoc={() => {
                  setEditingPoc(null);
                  setPocOpen(true);
                }}
                onEditPoc={(p) => {
                  setEditingPoc(p);
                  setPocOpen(true);
                }}
                onDeletePoc={deletePoc}
              />
            </div>
          </TabsContent>

          <TabsContent value="compliance" className="mt-0">
            <VendorCompliancePanel vendor={vendor} onChange={load} readOnly={!canEdit} />
          </TabsContent>

          <TabsContent value="documents" className="mt-0">
            <VendorAttachmentsTabs
              generalFiles={generalFiles}
              invoices={invoices}
              onUpload={canEdit ? uploadAttachment : null}
              onDownload={downloadAttachment}
              onDelete={canEdit ? deleteAttachment : null}
            />
          </TabsContent>

          <TabsContent value="activity" className="mt-0">
            <VendorAuditHistory vendorId={vendor.id} />
          </TabsContent>
        </Tabs>
      </div>

      {editOpen && (
        <VendorFormDialog
          open={editOpen}
          initial={vendor}
          onClose={() => setEditOpen(false)}
          onSubmit={onEditSubmit}
        />
      )}
      {pocOpen && (
        <VendorPocDialog
          open={pocOpen}
          initial={editingPoc}
          onClose={() => {
            setPocOpen(false);
            setEditingPoc(null);
          }}
          onSubmit={submitPoc}
        />
      )}
    </div>
  );
}
